import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import Dialog from './Dialog'
import Select from './Select'
import InputField from './InputField'
import TableMatchSelection from './table/TableMatchSelection'
import GridCircularProgress from './GridCircularProgress'
import { titlesIscrizioni } from '../settings/settings'
import { sortNumeriGara } from '../hooks/sort-functions'
import {
  useGetCalendariQuery,
  useGetImpostazioniAvanzateQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import { mailTo } from '../hooks/utils-functions'
import { getCategorie } from '../hooks/selection-get-functions'
import {
  formatIndirizzo,
  linkToGoogleMaps,
  formatBodyField,
} from '../hooks/utils-functions'

const AVANZ_FIELDS = ['GiorniAnticipoSpostamento']

const DialogCambioIndirizzo = ({ open, handleClose, triggerFunction }) => {
  const { codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCampionatoGironiPubblicati = useSelector(
    state => state.selection.selectedCampionatoGironiPubblicati,
  )

  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: !(selectedCampionato && selectedCampionatoGironiPubblicati) },
  )
  const { data: iscrizioni } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, codiceSocieta: codiceUtente },
    { skip: !(selectedCampionato && codiceUtente) },
  )

  const [selectedGare, setSelectedGare] = useState()
  const [presso, setPresso] = useState('')
  const [indirizzo, setIndirizzo] = useState('')
  const [citta, setCitta] = useState('')

  const resetIndirizzo = () => {
    setSelectedGare()
    setPresso('')
    setIndirizzo('')
    setCitta('')
  }

  const { data: availableGare, isLoading } = useGetCalendariQuery(
    { selectedCampionato, codiceUtente, tipologia: 'indirizzo' },
    { skip: !(selectedCampionato && codiceUtente) },
  )
  const { GiorniAnticipoSpostamento } = avanzate || {}

  const newIndirizzo = formatIndirizzo({ presso, indirizzo, citta })

  const [availableCategorie, setAvailableCategorie] = useState()
  const [availableSquadre, setAvailableSquadre] = useState()
  const [availableGareTeam, setAvailableGareTeam] = useState()
  const [selectedCategoria, setSelectedCategoria] = useState()
  const [selectedSquadra, setSelectedSquadra] = useState()

  // Set categorie
  useEffect(() => {
    if (open) setAvailableCategorie(getCategorie(availableGare))
  }, [availableGare, open])

  // Set squadre
  useEffect(() => {
    setAvailableSquadre(
      iscrizioniLight
        ?.filter(i =>
          availableGare
            ?.filter(a => a.categoria === selectedCategoria)
            ?.map(a => a.squadraCasaID)
            ?.includes(i.id),
        )
        ?.map(i => i.id),
    )
  }, [availableGare, iscrizioniLight, selectedCategoria])

  // Set gare
  useEffect(() => {
    setAvailableGareTeam(
      availableGare
        ?.filter(
          a =>
            a.squadraCasaID === selectedSquadra ||
            a.squadraTrasfertaID === selectedSquadra,
        )
        ?.sort((a, b) => sortNumeriGara(a.numeroGara, b.numeroGara)),
    )
  }, [availableGare, selectedSquadra])

  useEffect(() => {
    resetIndirizzo()
  }, [selectedSquadra])

  useEffect(() => {
    resetIndirizzo()
    if (!open) {
      setSelectedSquadra()
      setSelectedCategoria()
    }
  }, [open])

  const GridWarning = ({ text }) => (
    <Grid size={12}>
      <center>
        <b>
          <i>{text}</i>
        </b>
      </center>
    </Grid>
  )

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={() =>
        triggerFunction(selectedGare, presso, indirizzo, citta)
      }
      title={titlesIscrizioni.titleIndirizzo}
      textConfirm='Conferma'
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {((!iscrizioni || iscrizioni.length === 0) && (
          <Grid size={12}>Nessuna squadra iscritta a questo campionato.</Grid>
        )) || (
          <>
            <Grid size={12}>
              Il presente modulo permette di comunicare il cambio di indirizzo
              per una o più gare casalinghe.
              <br />
              Dopo aver scelto una categoria e una squadra, inserire il nuovo
              indirizzo di gioco e selezionare le gare a cui si desidera
              applicarlo.
              <br />
              <br />
              <b>
                La richiesta va inoltrata
                {GiorniAnticipoSpostamento
                  ? ` almeno ${GiorniAnticipoSpostamento} giorni `
                  : ' '}
                prima dell'orario di inizio gara e può essere effettuata solo
                dalla squadra di casa.
              </b>
              <br />
              <br />
              Per domande o chiarimenti scrivere a{' '}
              {mailTo(contatti?.MailPrincipale)}.
            </Grid>
            {(isLoading && <GridCircularProgress />) ||
              (!availableGare?.length && (
                <GridWarning text='Nessuna gara idonea.' />
              )) || (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                      label='Categoria'
                      value={selectedCategoria}
                      options={availableCategorie}
                      onChange={setSelectedCategoria}
                      disabled={!availableCategorie}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Select
                      label='Squadra'
                      value={selectedSquadra}
                      options={availableSquadre}
                      optionLabels={availableSquadre?.map(
                        x =>
                          iscrizioniLight?.find(i => i.id === x)?.nomeSquadra,
                      )}
                      onChange={setSelectedSquadra}
                      disabled={!selectedCategoria || !availableSquadre}
                    />
                  </Grid>
                  {selectedSquadra && (
                    <Grid container spacing={2} sx={{ width: '100%' }}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <center>
                          <b>Nuovo indirizzo</b>
                        </center>
                      </Grid>
                      <Grid container sx={{ width: '100%' }}>
                        <Grid size={{ xs: 12, md: 'grow' }}>
                          <InputField
                            id='presso'
                            value={presso}
                            onChange={i =>
                              setPresso(formatBodyField(i, 'upper'))
                            }
                            label='Istituto o centro sportivo'
                            adornment='place'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 'grow' }}>
                          <InputField
                            id='indirizzo'
                            value={indirizzo}
                            onChange={i =>
                              setIndirizzo(formatBodyField(i, 'upper'))
                            }
                            errorFunc={() => !indirizzo}
                            label='Indirizzo'
                            adornment='zone'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 'grow' }}>
                          <InputField
                            id='città'
                            value={citta}
                            onChange={i =>
                              setCitta(formatBodyField(i, 'upper'))
                            }
                            errorFunc={() => !citta}
                            label='Città'
                            adornment='city'
                          />
                        </Grid>
                        {newIndirizzo && (
                          <Grid size={12}>
                            {linkToGoogleMaps(newIndirizzo)}
                          </Grid>
                        )}
                      </Grid>
                      {availableGareTeam?.length > 0 && (
                        <TableMatchSelection
                          matches={availableGareTeam}
                          selectedGare={selectedGare}
                          setSelectedGare={setSelectedGare}
                          multiple={true}
                          showAddress={true}
                        />
                      )}
                    </Grid>
                  )}
                </>
              )}
          </>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogCambioIndirizzo
